import React, { useMemo, useState } from "react";
import { useHistory } from "react-router-dom";

import { makeStyles, alpha, lighten, useTheme } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import Chip from "@material-ui/core/Chip";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Tooltip from "@material-ui/core/Tooltip";
import CircularProgress from "@material-ui/core/CircularProgress";

import ChatBubbleOutlineIcon from "@material-ui/icons/ChatBubbleOutline";
import NoteOutlinedIcon from "@material-ui/icons/NoteOutlined";
import EventIcon from "@material-ui/icons/Event";
import AttachMoneyIcon from "@material-ui/icons/AttachMoney";
import ScheduleIcon from "@material-ui/icons/Schedule";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";

const typeConfig = {
  ticket: { label: "Atendimento", filter: "Atendimentos", color: "#2E9E5B", Icon: ChatBubbleOutlineIcon, linkLabel: "Abrir conversa" },
  note: { label: "Nota", filter: "Notas", color: "#E08600", Icon: NoteOutlinedIcon },
  appointment: { label: "Agendamento", filter: "Agendamentos", color: "#5C6BC0", Icon: EventIcon },
  opportunity: { label: "Negócio", filter: "Negócios", color: "#1E88E5", Icon: TrendingUpIcon, linkLabel: "Ver no CRM" },
  transaction: { label: "Financeiro", filter: "Financeiro", color: "#00897B", Icon: AttachMoneyIcon },
  schedule: { label: "Mensagem", filter: "Mensagens", color: "#8E24AA", Icon: ScheduleIcon },
};
const FILTER_ORDER = ["ticket", "note", "appointment", "opportunity", "transaction", "schedule"];

const GREEN = "#43A047";
const ORANGE = "#FB8C00";
const RED = "#E53935";
const BLUE = "#1E88E5";
const GREY = "#9E9E9E";

// rótulo em português + cor do selo, por tipo de evento e status gravado
const statusConfig = {
  ticket: {
    open: { label: "Aberto", color: GREEN },
    pending: { label: "Aguardando", color: ORANGE },
    closed: { label: "Finalizado", color: GREY },
  },
  appointment: {
    pendente: { label: "Pendente", color: ORANGE },
    confirmado: { label: "Confirmado", color: BLUE },
    concluido: { label: "Concluído", color: GREEN },
    faltou: { label: "Faltou", color: RED },
    cancelado: { label: "Cancelado", color: GREY },
  },
  transaction: {
    pending: { label: "Pendente", color: ORANGE },
    paid: { label: "Pago", color: GREEN },
    cancelled: { label: "Cancelado", color: GREY },
  },
  opportunity: {
    open: { label: "Em andamento", color: BLUE },
    won: { label: "Ganho", color: GREEN },
    lost: { label: "Perdido", color: RED },
  },
  schedule: {
    PENDENTE: { label: "Pendente", color: ORANGE },
    AGENDADA: { label: "Agendada", color: BLUE },
    ENVIADA: { label: "Enviada", color: GREEN },
    ERRO: { label: "Erro", color: RED },
  },
};

const getStatus = (type, status) =>
  statusConfig[type]?.[status] || { label: status, color: GREY };

const NOTE_MAX_LENGTH = 255; // coluna TicketNotes.note é VARCHAR(255)

// ---- datas ----------------------------------------------------------------

const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const DAY = 24 * 60 * 60 * 1000;

const timeOf = (d) =>
  d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });

const fullDate = (d) =>
  d.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const shortDate = (d, now) =>
  d.toLocaleDateString(
    "pt-BR",
    d.getFullYear() === now.getFullYear()
      ? { day: "2-digit", month: "2-digit" }
      : { day: "2-digit", month: "2-digit", year: "numeric" }
  );

// "há 2 horas", "ontem às 16:20", "amanhã às 14:00", "18/10 às 09:15"
const relativeLabel = (d, now) => {
  const diffMs = d.getTime() - now.getTime();
  const dayDiff = Math.round((startOfDay(d) - startOfDay(now)) / DAY);

  if (diffMs <= 0) {
    const min = Math.floor(-diffMs / 60000);
    if (min < 1) return "agora";
    if (min < 60) return `há ${min} min`;
    if (dayDiff === 0) {
      const h = Math.floor(min / 60);
      return `há ${h} ${h === 1 ? "hora" : "horas"}`;
    }
    if (dayDiff === -1) return `ontem às ${timeOf(d)}`;
  } else {
    if (dayDiff === 0) return `hoje às ${timeOf(d)}`;
    if (dayDiff === 1) return `amanhã às ${timeOf(d)}`;
  }
  return `${shortDate(d, now)} às ${timeOf(d)}`;
};

const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1);

// grupo de um evento passado: Hoje, Ontem, ou o mês ("Outubro de 2026")
const groupOf = (d, now) => {
  const dayDiff = Math.round((startOfDay(d) - startOfDay(now)) / DAY);
  if (dayDiff >= 0) return { key: "today", label: "Hoje" };
  if (dayDiff === -1) return { key: "yesterday", label: "Ontem" };
  return {
    key: `${d.getFullYear()}-${d.getMonth()}`,
    label: capitalize(d.toLocaleDateString("pt-BR", { month: "long", year: "numeric" })),
  };
};

// ---- textos ---------------------------------------------------------------

// o tipo já aparece como rótulo; tira o prefixo repetido do título
const displayTitle = (event) => {
  const title = event.title || "";
  if (event.type === "note") return "";
  return title.replace(/^(Negócio|Agendamento)\s+—\s+/, "");
};

const authorOf = (event) => {
  if (event.userName) return event.userName;
  if (event.type === "note") {
    const match = /^Nota de (.+)$/.exec(event.title || "");
    return match ? match[1] : "";
  }
  return "";
};

// ---- estilos --------------------------------------------------------------

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
  },
  header: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  titleRow: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  heading: {
    fontSize: 18,
    fontWeight: 700,
  },
  summary: {
    fontSize: 13,
    color: theme.palette.text.secondary,
  },
  composer: {
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "flex-start",
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  composerInput: {
    flex: 1,
    "& .MuiOutlinedInput-root": { borderRadius: 10 },
  },
  composerButton: {
    textTransform: "none",
    fontWeight: 600,
    borderRadius: 10,
    height: 40,
    marginTop: 8,
    padding: theme.spacing(0, 2),
    whiteSpace: "nowrap",
    [theme.breakpoints.down("xs")]: { marginTop: 0 },
  },
  filters: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
  },
  filterChip: {
    fontWeight: 500,
    height: 32,
    borderRadius: 16,
  },
  filterCount: {
    marginLeft: 6,
    opacity: 0.7,
    fontSize: 12,
  },
  groupHeader: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: theme.spacing(2, 0, 1),
  },
  groupLabel: {
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
    whiteSpace: "nowrap",
  },
  upcomingLabel: {
    color: theme.palette.primary.main,
  },
  groupLine: {
    flex: 1,
    height: 1,
    backgroundColor: theme.palette.divider,
  },
  item: {
    display: "flex",
    gap: 14,
    padding: theme.spacing(1.5, 1),
    margin: theme.spacing(0, -1),
    borderRadius: 10,
    transition: "background-color .12s",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  iconBox: {
    width: 36,
    height: 36,
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  body: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  titleLine: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
  },
  typeLabel: {
    fontSize: 12,
    fontWeight: 600,
  },
  title: {
    fontWeight: 600,
    fontSize: 15,
    color: theme.palette.text.primary,
    wordBreak: "break-word",
  },
  statusChip: {
    height: 22,
    fontSize: 12,
    fontWeight: 600,
  },
  description: {
    margin: 0,
    fontSize: 14,
    lineHeight: "20px",
    color: theme.palette.text.secondary,
    wordBreak: "break-word",
    overflow: "hidden",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
  },
  noteBox: {
    margin: "2px 0 0",
    padding: theme.spacing(1.25, 1.5),
    borderRadius: 10,
    fontSize: 14,
    lineHeight: "20px",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    color: theme.palette.text.primary,
    backgroundColor: alpha(typeConfig.note.color, 0.08),
    border: `1px solid ${alpha(typeConfig.note.color, 0.24)}`,
  },
  meta: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    flexWrap: "wrap",
    fontSize: 12,
    color: theme.palette.text.secondary,
  },
  metaLink: {
    padding: 0,
    minWidth: 0,
    fontSize: 12,
    fontWeight: 600,
    textTransform: "none",
    lineHeight: 1.4,
  },
  empty: {
    padding: theme.spacing(4, 2),
    textAlign: "center",
    color: theme.palette.text.secondary,
  },
}));

// ---- componentes ----------------------------------------------------------

const TimelineItem = ({ event, now, classes, onOpenLink }) => {
  const theme = useTheme();
  const config = typeConfig[event.type] || typeConfig.note;
  const { Icon } = config;
  const status = event.status && getStatus(event.type, event.status);
  // no tema escuro as cores de tipo/status ficam mais claras para manter o contraste
  const tone = (c) => (theme.palette.type === "dark" ? lighten(c, 0.35) : c);
  const typeColor = tone(config.color);
  const statusColor = status ? tone(status.color) : null;
  const date = new Date(event.date);
  const validDate = !Number.isNaN(date.getTime());
  const title = displayTitle(event);
  const author = authorOf(event);
  const isNote = event.type === "note";

  return (
    <article className={classes.item}>
      <span
        className={classes.iconBox}
        style={{ color: typeColor, backgroundColor: alpha(config.color, 0.16) }}
        aria-hidden="true"
      >
        <Icon fontSize="small" />
      </span>
      <div className={classes.body}>
        <div className={classes.titleLine}>
          <span className={classes.typeLabel} style={{ color: typeColor }}>
            {config.label}
          </span>
          {title && <span className={classes.title}>{title}</span>}
          {status && (
            <Chip
              size="small"
              label={status.label}
              className={classes.statusChip}
              style={{ backgroundColor: alpha(status.color, 0.16), color: statusColor }}
            />
          )}
        </div>
        {event.description && (
          <p className={isNote ? classes.noteBox : classes.description}>
            {event.description}
          </p>
        )}
        <div className={classes.meta}>
          {validDate && (
            <Tooltip title={fullDate(date)}>
              <span>{relativeLabel(date, now)}</span>
            </Tooltip>
          )}
          {author && (
            <>
              <span aria-hidden="true">·</span>
              <span>por {author}</span>
            </>
          )}
          {event.link && (
            <>
              <span aria-hidden="true">·</span>
              <Button
                color="primary"
                className={classes.metaLink}
                onClick={() => onOpenLink(event.link)}
              >
                {config.linkLabel || "Abrir"}
              </Button>
            </>
          )}
        </div>
      </div>
    </article>
  );
};

const GroupHeader = ({ label, classes, upcoming }) => (
  <div className={classes.groupHeader}>
    <span className={`${classes.groupLabel} ${upcoming ? classes.upcomingLabel : ""}`}>
      {label}
    </span>
    <span className={classes.groupLine} />
  </div>
);

const ContactTimeline = ({ events = [], onAddNote, onAddTask }) => {
  const classes = useStyles();
  const history = useHistory();
  const [filter, setFilter] = useState("all");
  const [draft, setDraft] = useState("");
  const [savingNote, setSavingNote] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const now = useMemo(() => new Date(), [events]);

  const counts = useMemo(() => {
    const result = {};
    events.forEach((e) => {
      result[e.type] = (result[e.type] || 0) + 1;
    });
    return result;
  }, [events]);

  const { upcoming, groups } = useMemo(() => {
    const visible = events.filter((e) => filter === "all" || e.type === filter);
    const future = [];
    const past = [];
    visible.forEach((e) => {
      const d = new Date(e.date);
      if (!Number.isNaN(d.getTime()) && d.getTime() > now.getTime()) future.push(e);
      else past.push(e);
    });
    future.sort((a, b) => new Date(a.date) - new Date(b.date));
    past.sort((a, b) => new Date(b.date) - new Date(a.date));

    const grouped = [];
    past.forEach((e) => {
      const d = new Date(e.date);
      const g = Number.isNaN(d.getTime())
        ? { key: "unknown", label: "Sem data" }
        : groupOf(d, now);
      const last = grouped[grouped.length - 1];
      if (last && last.key === g.key) last.items.push(e);
      else grouped.push({ ...g, items: [e] });
    });
    return { upcoming: future, groups: grouped };
  }, [events, filter, now]);

  const summary = useMemo(() => {
    const parts = [`${events.length} ${events.length === 1 ? "registro" : "registros"}`];
    const tickets = events.filter((e) => e.type === "ticket");
    if (tickets.length) {
      parts.push(`${tickets.length} ${tickets.length === 1 ? "atendimento" : "atendimentos"}`);
      const lastTicket = tickets
        .map((t) => new Date(t.date))
        .filter((d) => !Number.isNaN(d.getTime()) && d <= now)
        .sort((a, b) => b - a)[0];
      if (lastTicket) parts.push(`último atendimento ${relativeLabel(lastTicket, now)}`);
    }
    return parts.join(" · ");
  }, [events, now]);

  const handleOpenLink = (link) => history.push(link);

  const handleAddNote = async () => {
    const text = draft.trim();
    if (text.length < 3 || !onAddNote) return;
    setSavingNote(true);
    try {
      const ok = await onAddNote(text);
      if (ok !== false) setDraft("");
    } finally {
      setSavingNote(false);
    }
  };

  const filterKeys = FILTER_ORDER.filter((k) => counts[k]);
  const nothingVisible = upcoming.length === 0 && groups.length === 0;

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <div className={classes.titleRow}>
          <Typography component="h2" className={classes.heading}>
            Histórico
          </Typography>
          <span className={classes.summary}>{summary}</span>
        </div>

        {onAddNote && (
          <div className={classes.composer}>
            <TextField
              className={classes.composerInput}
              variant="outlined"
              size="small"
              margin="dense"
              placeholder="Escrever uma nota sobre este cliente…"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleAddNote();
                }
              }}
              multiline
              maxRows={4}
              disabled={savingNote}
              inputProps={{ maxLength: NOTE_MAX_LENGTH, "aria-label": "Nova nota" }}
              helperText={
                draft.length > NOTE_MAX_LENGTH - 40
                  ? `${draft.length}/${NOTE_MAX_LENGTH}`
                  : undefined
              }
            />
            <Button
              variant="contained"
              color="primary"
              disableElevation
              className={classes.composerButton}
              onClick={handleAddNote}
              disabled={savingNote || draft.trim().length < 3}
            >
              {savingNote ? <CircularProgress size={20} color="inherit" /> : "Adicionar nota"}
            </Button>
            {onAddTask && (
              <Button
                variant="outlined"
                className={classes.composerButton}
                onClick={onAddTask}
                style={{ color: "inherit" }}
              >
                Nova tarefa
              </Button>
            )}
          </div>
        )}

        {filterKeys.length > 1 && (
          <div className={classes.filters} role="group" aria-label="Filtrar histórico">
            <Chip
              clickable
              className={classes.filterChip}
              color={filter === "all" ? "primary" : "default"}
              variant={filter === "all" ? "default" : "outlined"}
              onClick={() => setFilter("all")}
              aria-pressed={filter === "all"}
              label={
                <>
                  Tudo<span className={classes.filterCount}>{events.length}</span>
                </>
              }
            />
            {filterKeys.map((key) => (
              <Chip
                key={key}
                clickable
                className={classes.filterChip}
                color={filter === key ? "primary" : "default"}
                variant={filter === key ? "default" : "outlined"}
                onClick={() => setFilter(key)}
                aria-pressed={filter === key}
                label={
                  <>
                    {typeConfig[key].filter}
                    <span className={classes.filterCount}>{counts[key]}</span>
                  </>
                }
              />
            ))}
          </div>
        )}
      </div>

      {events.length === 0 ? (
        <Typography variant="body2" className={classes.empty}>
          Nenhum registro no histórico deste contato ainda.
        </Typography>
      ) : nothingVisible ? (
        <Typography variant="body2" className={classes.empty}>
          Nenhum registro deste tipo.
        </Typography>
      ) : (
        <>
          {upcoming.length > 0 && (
            <section>
              <GroupHeader label="Próximos" classes={classes} upcoming />
              {upcoming.map((event) => (
                <TimelineItem
                  key={event.id}
                  event={event}
                  now={now}
                  classes={classes}
                  onOpenLink={handleOpenLink}
                />
              ))}
            </section>
          )}
          {groups.map((group) => (
            <section key={group.key}>
              <GroupHeader label={group.label} classes={classes} />
              {group.items.map((event) => (
                <TimelineItem
                  key={event.id}
                  event={event}
                  now={now}
                  classes={classes}
                  onOpenLink={handleOpenLink}
                />
              ))}
            </section>
          ))}
        </>
      )}
    </div>
  );
};

export default ContactTimeline;
